import { useShop } from '@/contexts/ShopContext';
import { useSound } from '@/contexts/SoundContext';
import Character3D, { type CharacterConfig } from '../game/elements/Character3D';
import { PixelIcon } from '../ui';
import { motion } from 'motion/react';
import { useState } from 'react';

// Deep partial type helper
type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

interface CharacterShopItem {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: 'coins' | 'gems';
  config: DeepPartial<CharacterConfig>;
  unlocked: boolean;
  owned: boolean;
}

// Character shop items
const characterShopItems: CharacterShopItem[] = [
  {
    id: 'default_hero',
    name: 'Default Hero',
    description: 'The classic red cube hero. Reliable and balanced.',
    price: 0,
    currency: 'coins',
    config: {}, // Uses all defaults
    unlocked: true,
    owned: true, // Default character is always owned
  },
  {
    id: 'tiny_blue',
    name: 'Tiny Blue',
    description: 'Small but mighty! This compact hero is perfect for tight spaces.',
    price: 100,
    currency: 'coins',
    config: {
      sizes: {
        characterSize: 0.5,
        cubeSize: 0.95,
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #4a90e2 0%, #357abd 50%, #2968a3 100%)',
          back: 'linear-gradient(135deg, #357abd 0%, #2968a3 50%, #1e5189 100%)',
          top: 'linear-gradient(135deg, #5ba0f2 0%, #4a90e2 50%, #357abd 100%)',
          bottom: 'linear-gradient(135deg, #2968a3 0%, #1e5189 50%, #143a5f 100%)',
          left: 'linear-gradient(135deg, #4080d0 0%, #3570c0 50%, #2a60b0 100%)',
          right: 'linear-gradient(135deg, #4585d5 0%, #3a75c5 50%, #2f65b5 100%)',
        },
      },
      face: {
        eyes: {
          size: '35%',
        },
      },
    },
    unlocked: true,
    owned: false,
  },
  {
    id: 'chonky_green',
    name: 'Chonky Green',
    description: 'Big and bold! This hefty hero has extra presence on the board.',
    price: 200,
    currency: 'coins',
    config: {
      sizes: {
        characterSize: 0.95,
        cubeSize: 0.85,
        shadow: {
          width: 0.9,
          height: 0.25,
          offsetY: 0.12,
        },
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #4caf50 0%, #45a049 50%, #388e3c 100%)',
          back: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 50%, #1b5e20 100%)',
          top: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 50%, #43a047 100%)',
          bottom: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 50%, #0d3f15 100%)',
          left: 'linear-gradient(135deg, #43a047 0%, #3b9040 50%, #338037 100%)',
          right: 'linear-gradient(135deg, #48a84c 0%, #409843 50%, #38883b 100%)',
        },
      },
      face: {
        eyes: {
          size: '15%',
          gap: '35%',
        },
        mouth: {
          width: '40%',
        },
      },
    },
    unlocked: true,
    owned: false,
  },
  {
    id: 'sleek_purple',
    name: 'Sleek Purple',
    description: 'Elegant and mysterious. This refined hero has a magical aura.',
    price: 150,
    currency: 'gems',
    config: {
      sizes: {
        characterSize: 0.75,
        cubeSize: 0.98,
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #9c27b0 0%, #8e24aa 50%, #7b1fa2 100%)',
          back: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 50%, #4a148c 100%)',
          top: 'linear-gradient(135deg, #ab47bc 0%, #9c27b0 50%, #8e24aa 100%)',
          bottom: 'linear-gradient(135deg, #6a1b9a 0%, #4a148c 50%, #2a0a5c 100%)',
          left: 'linear-gradient(135deg, #8e24aa 0%, #7e22a0 50%, #6e1f96 100%)',
          right: 'linear-gradient(135deg, #9125ac 0%, #8123a2 50%, #712098 100%)',
        },
        effects: {
          shadow: 'radial-gradient(ellipse, rgba(156, 39, 176, 0.4) 0%, transparent 70%)',
        },
      },
      face: {
        eyes: {
          size: '22%',
          shine: {
            size: '40%',
            position: { top: '15%', left: '20%' },
          },
        },
        mouth: {
          width: '30%',
          heights: {
            neutral: '12%',
            happy: '20%',
            shocked: '25%',
          },
        },
      },
    },
    unlocked: true,
    owned: false,
  },
  {
    id: 'golden_glow',
    name: 'Golden Glow',
    description: 'Radiant and prestigious! This golden hero shines with victory.',
    price: 500,
    currency: 'gems',
    config: {
      sizes: {
        characterSize: 0.8,
        cubeSize: 0.9,
      },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #ffc107 0%, #ffb300 50%, #ffa000 100%)',
          back: 'linear-gradient(135deg, #ffa000 0%, #ff8f00 50%, #ff6f00 100%)',
          top: 'linear-gradient(135deg, #ffca28 0%, #ffc107 50%, #ffb300 100%)',
          bottom: 'linear-gradient(135deg, #ff8f00 0%, #ff6f00 50%, #e65100 100%)',
          left: 'linear-gradient(135deg, #ffb300 0%, #ffa300 50%, #ff9300 100%)',
          right: 'linear-gradient(135deg, #ffb800 0%, #ffa800 50%, #ff9800 100%)',
        },
      },
      backFace: {
        symbol: '*',
        sizeScale: 0.4,
        opacity: 0.6,
      },
    },
    unlocked: true,
    owned: false,
  },
];

interface CharacterShopSectionProps {
  filter: string;
}

const CharacterShopSection: React.FC<CharacterShopSectionProps> = () => {
  const { shopState, purchaseCharacter, equipCharacter } = useShop();
  const { playButtonClick } = useSound();
  const [selectedCharacter, setSelectedCharacter] = useState<string>(shopState.equippedCharacter || 'default_hero');

  const handlePurchaseCharacter = (character: CharacterShopItem) => {
    const success = purchaseCharacter(character.id, character.price, character.currency);
    
    if (success) {
      playButtonClick();
      console.log(`Purchased ${character.name}!`);
    } else {
      playButtonClick();
      console.log('Cannot purchase character - insufficient funds or already owned');
    }
  };

  const handleEquipCharacter = (character: CharacterShopItem) => {
    const success = equipCharacter(character.id);
    
    if (success) {
      playButtonClick();
      setSelectedCharacter(character.id);
      console.log(`Equipped ${character.name}!`);
    } else {
      playButtonClick();
      console.log('Cannot equip character - not owned');
    }
  };

  const canAfford = (character: CharacterShopItem) => {
    const playerBalance = character.currency === 'coins' ? shopState.coins : shopState.gems;
    return playerBalance >= character.price;
  };

  return (
    <div className="character-shop">
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
        gap: '20px',
        padding: '10px 0'
      }}>
        {characterShopItems.map((character) => {
          const isOwned = shopState.ownedCharacters.includes(character.id);
          const isEquipped = shopState.equippedCharacter === character.id;
          
          return (
          <motion.div
            key={character.id}
            className="character-shop-card"
            style={{
              border: `2px solid ${selectedCharacter === character.id ? 'var(--pixel-primary)' : 'var(--pixel-border)'}`,
              borderRadius: '15px',
              padding: '15px',
              background: 'var(--pixel-bg-secondary)',
              cursor: 'pointer',
              position: 'relative',
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedCharacter(character.id)}
          >
            {/* Character Preview */}
            <div style={{
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '10px 0',
              background: 'var(--pixel-bg)',
              borderRadius: '10px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Character3D
                position={[0, 0]}
                direction={null}
                isMoving={false}
                cellSize={60}
                hasCollided={false}
                gameState="playing"
                config={character.config}
              />
            </div>

            {/* Character Info */}
            <div style={{ textAlign: 'center', marginBottom: '15px' }}>
              <h3 style={{ 
                margin: '0 0 5px 0', 
                fontSize: '16px',
                color: 'var(--pixel-text)'
              }}>
                {character.name}
              </h3>
              <p style={{ 
                margin: '0', 
                fontSize: '12px', 
                color: 'var(--pixel-text-secondary)',
                lineHeight: '1.3'
              }}>
                {character.description}
              </p>
            </div>

            {/* Purchase Button */}
            {isOwned ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEquipCharacter(character);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '10px',
                  background: isEquipped ? 'var(--pixel-success)' : 'var(--pixel-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  gap: '5px'
                }}
              >
                <PixelIcon name={isEquipped ? "check" : "user"} size={16} />
                {isEquipped ? 'Equipped' : 'Equip'}
              </button>
            ) : character.price === 0 ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px',
                background: 'var(--pixel-info)',
                color: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Free
              </div>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePurchaseCharacter(character);
                }}
                disabled={!canAfford(character)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '10px',
                  background: canAfford(character) ? 'var(--pixel-primary)' : 'var(--pixel-disabled)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: canAfford(character) ? 'pointer' : 'not-allowed',
                  gap: '5px'
                }}
              >
                <PixelIcon 
                  name={character.currency === 'coins' ? 'coin' : 'gem'} 
                  size={16} 
                  color={character.currency === 'coins' ? '#f39c12' : '#9b59b6'} 
                />
                {character.price}
              </button>
            )}
          </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default CharacterShopSection;